import {
  ActipayUser,
  AllowedUserData,
  ConsentState,
  UserProperties,
} from '../../interfaces/tracking/TrackingPayload';
import { CompanySize } from '../../interfaces/tracking/TrackingTypes';
import { TRACKING_VARIABLES } from '../../constants';

const COOKIE_DOMAIN = '.actipay.fr';

export const getActipayUserFromLS = (): ActipayUser => {
  const actipayUser =
    localStorage.getItem(TRACKING_VARIABLES.ACTIPAY_USER) || JSON.stringify({});
  return JSON.parse(actipayUser);
};

export const setActipayUserToLS = (user: ActipayUser) => {
  localStorage.setItem(TRACKING_VARIABLES.ACTIPAY_USER, JSON.stringify(user));
};

export const getStapeUserId = () => {
  return (getCookie('stape_user_id') || getCookie('_stape_uid')) ?? 'unknown';
};

export const generateEventId = () => {
  return 'act_' + crypto.randomUUID();
};

export const getConsentState = (): ConsentState => {
  const cookie: any = getCookie('CookieScriptConsent') ?? {};
  let decodedCookie: any = {};
  try {
    decodedCookie = JSON.parse(decodeURIComponent(cookie)).googleconsentmap;
  } catch {}

  const map = (v: string | undefined) =>
    v && ['granted', 'performance', 'functionality', 'targeting'].includes(v)
      ? 'granted'
      : 'denied';

  return {
    ad_storage: map(decodedCookie.ad_storage),
    analytics_storage: map(decodedCookie.analytics_storage),
    ad_user_data: map(decodedCookie.ad_user_data),
    ad_personalization: map(decodedCookie.ad_personalization),
  };
};

const initActipayUser = () => {
  const actipayUser = getActipayUserFromLS();
  if (!actipayUser) {
    setActipayUserToLS({ user_data: {}, user_properties: {} });
  }
};

export const getUserId = () => {
  return getActipayUserFromLS().user_data?.id ?? 'anonymous';
};

export const setUserId = (id: string) => {
  initActipayUser();
  const actipayUser = getActipayUserFromLS();
  if (!actipayUser.user_data?.id) {
    setActipayUserToLS({
      ...actipayUser,
      user_data: { ...actipayUser.user_data, id: id || 'unknown' },
    });
  }
};

export const getUserProperties = (): UserProperties => {
  const p: UserProperties = getActipayUserFromLS().user_properties ?? {};

  return {
    lead_status: p.lead_status ?? 'prospect',
    offer_type: p.offer_type ?? null,
    commitment_duration: p.commitment_duration ?? null,
    lifetime_value: p.lifetime_value ?? 0,
    company_size: p.company_size ?? null,
    industry: p.industry ?? null,
    is_representative: p.is_representative ?? null,
  };
};

export const setUserProperties = (properties: UserProperties): void => {
  initActipayUser();
  const actipayUser = getActipayUserFromLS();

  setActipayUserToLS({
    ...actipayUser,
    user_properties: {
      ...actipayUser.user_properties,
      ...properties,
    },
  });
};

export const getUserData = (): AllowedUserData => {
  const p: AllowedUserData = getActipayUserFromLS().user_data ?? {};

  return {
    first_name: p.first_name ?? null,
    last_name: p.last_name ?? null,
    company: p.company ?? null,
    email: p.email ?? null,
    phone: p.phone ?? null,
    siren: p.siren ?? null,
    address: p.address ?? null,
  };
};

export const setUserData = (data: AllowedUserData): void => {
  initActipayUser();
  const actipayUser = getActipayUserFromLS();
  setActipayUserToLS({
    ...actipayUser,
    user_data: {
      ...actipayUser.user_data,
      ...data,
    },
  });
};

export const getContextData = () => {
  return {
    user_agent: navigator.userAgent,
    language: navigator.language,
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer || null,
  };
};

export const getAttribution = () => {
  const LS = window.localStorage;
  const url = new URLSearchParams(window.location.search);
  const now = Date.now();
  const EXPIRY = 90 * 24 * 60 * 60 * 1000; // 90 jours

  const captureIf = (key, param) => {
    const v = url.get(param);
    if (v) LS.setItem('act_' + key, JSON.stringify({ v, t: now }));
    const stored = LS.getItem('act_' + key);
    if (stored) {
      try {
        const { v: val, t } = JSON.parse(stored);
        if (now - t < EXPIRY) return val;
      } catch {}
    }
    return null;
  };

  return {
    // Google Ads
    gclid: captureIf('gclid', 'gclid'),
    gbraid: captureIf('gbraid', 'gbraid'),
    wbraid: captureIf('wbraid', 'wbraid'),
    // LinkedIn Ads
    li_fat_id: captureIf('li_fat_id', 'li_fat_id'),
    // Microsoft / Bing (si activé plus tard)
    msclkid: captureIf('msclkid', 'msclkid'),
    // Facebook Click ID (si activé plus tard)
    fbclid: captureIf('fbclid', 'fbclid'),
    // UTMs first-touch
    utm_source: captureIf('utm_source', 'utm_source'),
    utm_medium: captureIf('utm_medium', 'utm_medium'),
    utm_campaign: captureIf('utm_campaign', 'utm_campaign'),
    utm_term: captureIf('utm_term', 'utm_term'),
    utm_content: captureIf('utm_content', 'utm_content'),
    // Referrer et landing page (first-touch)
    landing_page: LS.getItem('act_landing_page') || window.location.href,
    initial_referrer:
      LS.getItem('act_initial_referrer') || document.referrer || '(direct)',
  };
};

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

export const setCookie = (
  name: string,
  value: string | number | boolean,
  ms: number,
) => {
  const date = new Date();
  date.setTime(date.getTime() + ms);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; domain=${COOKIE_DOMAIN}; path=/; SameSite=Lax`;
};

export function getGAClientId(): string | null {
  const rawCookie = getCookie('_ga');
  if (!rawCookie) return null;
  const match = rawCookie.match(/GA1\.\d+\.(\d+\.\d+)/);
  return match ? match[1] : rawCookie;
}

export const toCompanySize = (sizeStr: string): CompanySize => {
  const size = parseInt(sizeStr, 10);
  if (isNaN(size) || size <= 10) {
    return '1-10';
  }
  if (size <= 50) {
    return '11-50';
  }
  if (size <= 200) {
    return '51-200';
  }
  if (size <= 500) {
    return '201-500';
  }
  return '500+';
};
